import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import WrButton from 'ember-wiser/components/button';
import type { HomePageAlertType } from 'parog-web/models/alert-triggered-journal-event';
import type NotificationTriggeredJournalEvent from 'parog-web/models/notification-triggered-journal-event';
import DropdownButtonBase, {
  type Signature
} from 'ui-kit/components/dropdown-button-base';

interface ActionButtonBaseSignature<
  Item = HomePageAlertType | NotificationTriggeredJournalEvent
> {
  Args: {
    item: Item;
    dataTestButton: string;
    color?: string;
    onProcessed: (
      item: Item,
      actionTaken: string | undefined,
      e?: Event | undefined
    ) => void;
  };
  Blocks: {
    dropdownItems: [DropdownItem: Signature['Blocks']['items'][0]];
  };
}

const customStylesBecauseOfDropdownButtonBase = 'relative -top-2';

export default class ActionsButtonBase<
  Item = HomePageAlertType | NotificationTriggeredJournalEvent
> extends Component<ActionButtonBaseSignature<Item>> {
  /**
   * ⚡ Perf flag
   *
   * Should we render the FUI dropdown markup and initialize the FUI dropdown menu?
   *
   * When false, renders a simple WrButton instead that will toggle this flag when clicked
   *
   * When true, renders the FUI dropdown and initializes it
   *
   * Defaults to false
   *
   * Stats as of 2025-02-25: rendering 2000 alerts
   *  - shouldRenderDropdown = false => ~2000ms
   *  - shouldRenderDropdown = true => ~3000ms

   * */
  @tracked shouldRenderDropdown = false;

  /**
   * Action bound to the Button rendered when shouldRenderDropdown is false
   *
   * This action sets shouldRenderDropdown to true.
   *
   * This will have the effect of rendering the FUI dropdown markup and initialize it.
   *
   * When rendered, the dropdown button will be clicked to display the dropdown menu.
   * */
  renderDropdown = (evt: Event) => {
    this.shouldRenderDropdown = true;

    const btn = evt.currentTarget as HTMLButtonElement;
    if (!btn) return;
    const parent = btn.parentNode;
    if (!parent) return;

    requestAnimationFrame(() => {
      const dropdownBtn = parent.querySelector(
        `[data-test-dropdown=${this.args.dataTestButton}] button`
      ) as HTMLButtonElement;
      if (dropdownBtn) dropdownBtn.click();
    });
  };

  <template>
    {{#if this.shouldRenderDropdown}}
      <DropdownButtonBase
        @onClick={{fn @onProcessed @item}}
        @alignMenuLeft={{true}}
        data-test-dropdown='option'
      >
        <:button>
          <div data-test-dropdown='{{@dataTestButton}}'>
            {{!
            We are using DropdownButtonBase instead of the currently missing WrSelect
            That's why we need to explicity set width, height and position

            This will go away when migrating to WrSelect
          }}
            <WrButton
              @variant='outlined-mini'
              @icon='Ellipsis'
              class='{{customStylesBecauseOfDropdownButtonBase}}
                my-2 [&>svg]:h-6 [&>svg]:w-6
                {{if @color @color "text-grey"}}'
            />
          </div>
        </:button>

        <:items as |DropdownItem|>
          {{yield DropdownItem to='dropdownItems'}}
        </:items>
      </DropdownButtonBase>
    {{else}}
      <WrButton
        data-test-dropdown={{@dataTestButton}}
        aria-label="Dropdown des actions d'une alerte"
        {{on 'click' this.renderDropdown}}
        @variant='outlined-mini'
        @icon='Ellipsis'
        class='{{customStylesBecauseOfDropdownButtonBase}}
          text-grey mb-2 mt-4 [&>svg]:h-6 [&>svg]:w-6'
      />
    {{/if}}
  </template>
}
